import React from "react";
//#region types
import { LocalizationHelperInstance } from "utils/helpers";
import { IconDefinitionConstants } from "utils/constants";
import { DialogEnums, StyleEnums } from "models/enums";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import CreateLeaveFormContainer from "./leaveFormContainer";
import { Icon, RoundButton, useFloatingMessages } from "components/common";
import { Grid } from "@mui/material";
import { useSalaryDeviationsService } from "serviceHooks";
import useIsReadOnly from "./useIsReadOnly";
//#endregion
import cssExports from "theme/styles/main.module.scss";

function useCreateLeaveDialog(onLeaveSubmitSuccess?: () => void) {
  const salaryDeviationsService = useSalaryDeviationsService();
  const { enqueueMessage } = useFloatingMessages();

  const { getIsReadOnly } = useIsReadOnly();

  const onDelete = async (closeDialog: () => void, salaryDeviationId: number) => {
    salaryDeviationsService.deleteSalaryDeviation(salaryDeviationId).then((res) => {
      closeDialog();
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteLeaveSuccess),
        StyleEnums.DisplayType.Success
      );
      onLeaveSubmitSuccess && onLeaveSubmitSuccess();
    });
  };

  const getLeaveDialog = (options?: {
    salaryDeviationId?: number;
    monthState?: WintSuperkollAPIModel.SalaryDeviationMonthState;
    yearMonth?: number;
    personId?: number;
    personName?: string;
    isAdminOnlyType?: boolean;
  }): DialogProps => {
    const isReadOnly = getIsReadOnly({
      isAdminOnlyType: options?.isAdminOnlyType ?? false,
      isMonthDisabled: salaryDeviationsService.isMonthDisabled(options?.monthState),
    });

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(
        options?.salaryDeviationId
          ? TranslationKeys.SalaryDeviation.EditTimeDeviation
          : TranslationKeys.SalaryDeviation.AddTimeDeviation
      ),
      titleIcon: IconDefinitionConstants.WCalendar,
      actions: !isReadOnly
        ? [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Send),
              successMessage: TranslationKeys.SalaryDeviation.CreatedLeaveSuccess,
            },
          ]
        : undefined,
      footer:
        options?.salaryDeviationId && !isReadOnly
          ? (closeDialog, isValid) => (
              <Grid item xs alignItems="flex-end">
                <Grid item>
                  <RoundButton
                    className={cssExports.delete}
                    onClick={onDelete.bind(null, closeDialog, options?.salaryDeviationId!)}
                    disabled={!isValid}
                  >
                    <Icon icon={IconDefinitionConstants.WTrashAlt} />
                  </RoundButton>
                </Grid>
              </Grid>
            )
          : undefined,
      dialogType: DialogEnums.DialogType.Content,
      children: (
        <CreateLeaveFormContainer
          salaryDeviationId={options?.salaryDeviationId}
          monthState={options?.monthState}
          yearMonth={options?.yearMonth}
          personId={options?.personId}
          personName={options?.personName}
        />
      ),
      maxWidth: "sm",
      onSubmit: onLeaveSubmitSuccess,
    };
  };

  return getLeaveDialog;
}

export default useCreateLeaveDialog;
